import type { Dispatch, SetStateAction } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import type { LessonAiDraft } from "../../auth/api-client";
import { AutoResizeTextarea } from "../../components/AutoResizeTextarea";
import { LessonCodeEditor } from "./LessonCodeEditor";

export interface LessonInstructionsSectionProps {
  readonly draft: LessonAiDraft;
  readonly setDraft: Dispatch<SetStateAction<LessonAiDraft>>;
  readonly updateStep: (index: number, patch: Partial<LessonAiDraft["tourSteps"][number]>) => void;
}

export function LessonInstructionsSection({
  draft,
  setDraft,
  updateStep,
}: LessonInstructionsSectionProps) {
  return (
    <div className="lesson-ws-section">
      <p className="muted small lesson-ws-section-lede">
        Step-by-step guidance students read beside the editor. Markdown works in body text (headings,
        bold, links, lists, tables, fenced code). Reference code is a syntax hint only, never the answer.
      </p>

      {draft.tourSteps.map((step, i) => (
        <fieldset key={i} className="teacher-step-fieldset">
          <legend className="fineprint">Step {i + 1}</legend>
          <label className="field">
            <span className="field-label">Title</span>
            <input
              className="input"
              value={step.title}
              onChange={(ev) => updateStep(i, { title: ev.target.value })}
              placeholder="Step title"
            />
          </label>
          <label className="field">
            <span className="field-label">Body (one instruction per line)</span>
            <AutoResizeTextarea
              className="input"
              minRows={3}
              value={step.body.join("\n")}
              onChange={(ev) => updateStep(i, { body: ev.target.value.split("\n") })}
              placeholder="What should the student do in this step?"
            />
          </label>
          <label className="field">
            <span className="field-label">Reference code (optional)</span>
            <LessonCodeEditor
              value={step.referenceCode ?? ""}
              onChange={(v) => updateStep(i, { referenceCode: v || null })}
              ariaLabel={`Reference code for step ${i + 1}`}
              minHeight="6rem"
            />
          </label>
          <button
            type="button"
            className="btn secondary danger tight"
            disabled={draft.tourSteps.length <= 1}
            onClick={() =>
              setDraft((d) => ({
                ...d,
                tourSteps: d.tourSteps.filter((_, j) => j !== i),
              }))
            }
          >
            <FontAwesomeIcon icon={faTrash} /> Remove step
          </button>
        </fieldset>
      ))}

      <button
        type="button"
        className="btn secondary"
        onClick={() =>
          setDraft((d) => ({
            ...d,
            tourSteps: [...d.tourSteps, { title: "New step", body: [""], referenceCode: null }],
          }))
        }
      >
        <FontAwesomeIcon icon={faPlus} /> Add step
      </button>
    </div>
  );
}
